import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";

import { UserRole, ViewKind } from "~/shared/enums";

const TASK_LIST_VIEWS = [
  {
    path: "my-tasks",
    name: "my_tasks",
    title: "My tasks",
    propsAdtl: { viewKind: ViewKind.MY_TASKS },
  },
  {
    path: "trash",
    name: "trash",
    title: "Trash",
    propsAdtl: { viewKind: ViewKind.TRASH },
  },
  {
    path: "v/:viewDuid([a-zA-Z0-9]{12})",
    name: "view",
    title: "View",
  },
  {
    path: "d/:dartboardDuid([a-zA-Z0-9]{12})",
    name: "dartboard",
    title: "Dartboard",
  },
];

const ROUTE_IMPORTS = {
  AgentDetail: () => import("~/components/agent/AgentDetail.vue"),
  AgentList: () => import("~/components/agent/AgentList.vue"),
  ConnectorOauthFinishedView: () => import("~/views/ConnectorOauthFinishedView.vue"),
  ConsoleView: () => import("~/views/ConsoleView.vue"),
  CookiesComponent: () => import("~/components/legal/Cookies.vue"),
  DashboardsRootView: () => import("~/views/DashboardsRootView.vue"),
  DashboardView: () => import("~/views/intelligence/DashboardView.vue"),
  DefaultRedirectView: () => import("~/views/DefaultRedirectView.vue"),
  DocView: () => import("~/views/DocView.vue"),
  EntityRedirectView: () => import("~/views/EntityRedirectView.vue"),
  ExternalFormView: () => import("~/views/ExternalFormView.vue"),
  FolderRootView: () => import("~/views/FolderRootView.vue"),
  FolderView: () => import("~/views/FolderView.vue"),
  GoogleOauthView: () => import("~/views/auth/GoogleOauthView.vue"),
  HomePageView: () => import("~/views/HomePageView.vue"),
  HomeView: () => import("~/views/HomeView.vue"),
  InboxView: () => import("~/views/InboxView.vue"),
  JoinWorkspaceView: () => import("~/views/auth/JoinWorkspaceView.vue"),
  LastRedirectView: () => import("~/views/LastRedirectView.vue"),
  LatencyView: () => import("~/views/LatencyView.vue"),
  LegalView: () => import("~/views/LegalView.vue"),
  LoginView: () => import("~/views/auth/LoginView.vue"),
  ModeRouteView: () => import("~/views/ModeRouteView.vue"),
  PageNotFound: () => import("~/components/dumb/PageNotFound.vue"),
  PrivacyComponent: () => import("~/components/legal/Privacy.vue"),
  PublicViewView: () => import("~/views/PublicViewView.vue"),
  RedirectView: () => import("~/views/RedirectView.vue"),
  ResetPasswordView: () => import("~/views/auth/ResetPasswordView.vue"),
  SignUpView: () => import("~/views/auth/SignUpView.vue"),
  SuperuserDashboardView: () => import("~/views/SuperuserDashboardView.vue"),
  TaskListView: () => import("~/views/TaskListView.vue"),
  TermsComponent: () => import("~/components/legal/Terms.vue"),
  VerifyEmailView: () => import("~/views/auth/VerifyEmailView.vue"),
  ViewsRootView: () => import("~/views/ViewsRootView.vue"),
} as const;

// Prefetch all the components
Object.values(ROUTE_IMPORTS).forEach((importPromise) => {
  if (import.meta.env.MODE !== "test") {
    importPromise();
  }
});

const ROUTES: RouteRecordRaw[] = [
  {
    path: "/",
    children: [
      {
        path: "sign-up",
        name: "sign_up",
        component: ROUTE_IMPORTS.SignUpView,
        meta: {
          title: "Sign up for Dart",
          requiresAuth: false,
          setsPageLoadedManually: true,
          redirectIfLoggedIn: true,
        },
      },
      {
        path: "app-sumo-sign-up",
        name: "app_sumo_sign_up",
        component: ROUTE_IMPORTS.SignUpView,
        meta: {
          title: "Sign up for Dart via AppSumo",
          requiresAuth: false,
          setsPageLoadedManually: true,
        },
      },
      {
        path: "login",
        name: "login",
        component: ROUTE_IMPORTS.LoginView,
        meta: {
          title: "Log in to Dart",
          requiresAuth: false,
          setsPageLoadedManually: true,
          redirectIfLoggedIn: true,
        },
      },
      {
        path: "google-oauth",
        name: "google_oauth",
        component: ROUTE_IMPORTS.GoogleOauthView,
        meta: {
          title: "Logging into Dart with Google",
          requiresAuth: false,
          setsPageLoadedManually: true,
        },
      },
      {
        path: "verify-email",
        name: "verify_email",
        component: ROUTE_IMPORTS.VerifyEmailView,
        meta: {
          title: "Verifying email with Dart",
          requiresAuth: false,
          setsPageLoadedManually: true,
          redirectIfLoggedIn: true,
        },
      },
      {
        path: "join",
        name: "join",
        component: ROUTE_IMPORTS.JoinWorkspaceView,
        meta: {
          title: "Accept Dart invitation",
          requiresAuth: false,
          setsPageLoadedManually: true,
          redirectIfLoggedIn: true,
        },
      },
      {
        path: "reset-password",
        name: "reset_password",
        component: ROUTE_IMPORTS.ResetPasswordView,
        meta: {
          title: "Reset Dart password",
          requiresAuth: false,
          setsPageLoadedManually: true,
          redirectIfLoggedIn: true,
        },
      },
      {
        path: "logout",
        name: "logout",
        component: ROUTE_IMPORTS.LoginView, // don't want this to be a redirect because otherwise nav logic won't be run
      },
    ],
  },
  {
    path: "/latency",
    name: "latency",
    component: ROUTE_IMPORTS.LatencyView,
    meta: {
      title: "Latency",
    },
  },
  {
    path: "/p",
    children: [
      {
        path: "r/:formDuid([a-zA-Z0-9]{12}):slugSep?:slug?",
        name: "public_form",
        component: ROUTE_IMPORTS.ExternalFormView,
        meta: {
          title: "Form",
          setsPageTitleManually: true,
          setsPageLoadedManually: true,
          requiresAuth: false,
        },
      },
      {
        path: "v/:viewDuid([a-zA-Z0-9]{12}):slugSep?:slug?/:taskDuidAndSlug?",
        name: "public_view",
        component: ROUTE_IMPORTS.PublicViewView,
        meta: {
          title: "View",
          setsPageTitleManually: true,
          setsPageLoadedManually: true,
          requiresAuth: false,
        },
      },
    ],
  },
  {
    path: "/console",
    name: "console",
    component: ROUTE_IMPORTS.ConsoleView,
    meta: {
      title: "Console",
    },
  },
  {
    path: "/superuser-dashboard",
    name: "superuser_dashboard",
    redirect: { name: "superuser_dashboard_tab", params: { tabId: "home" } },
    meta: {
      title: "Superuser dashboard",
    },
  },
  {
    path: "/superuser-dashboard/:tabId",
    name: "superuser_dashboard_tab",
    component: ROUTE_IMPORTS.SuperuserDashboardView,
    meta: {
      title: "Superuser dashboard",
    },
  },
  {
    path: "/",
    component: ROUTE_IMPORTS.HomeView,
    children: [
      {
        path: "",
        name: "last",
        component: ROUTE_IMPORTS.LastRedirectView,
        meta: {
          title: "Last",
          setsPageLoadedManually: true,
          setsPageTitleManually: true,
        },
      },
      {
        path: "default",
        name: "default",
        component: ROUTE_IMPORTS.DefaultRedirectView,
        meta: {
          title: "Default",
          setsPageLoadedManually: true,
          setsPageTitleManually: true,
        },
      },
      {
        path: "setup/:setupStep",
        component: ROUTE_IMPORTS.ModeRouteView,
        name: "build_setup",
        meta: {
          title: "Set up",
          setsPageLoadedManually: true,
          setsPageTitleManually: true,
          modeRoute: true,
        },
      },
      ...TASK_LIST_VIEWS.map(({ path, name, title, propsAdtl }) => ({
        path: `${path}:slugSep?:slug?/:taskDuidAndSlug?`,
        name,
        component: ROUTE_IMPORTS.TaskListView,
        props: (route: RouteLocationNormalized) => ({ ...route.params, ...(propsAdtl ?? {}) }),
        meta: {
          title,
          setsPageTitleManually: true,
          setsPageLoadedManually: true,
        },
      })),
      {
        path: "search/:searchDuid([a-zA-Z0-9]{12})?/:taskDuidAndSlug?",
        name: "search",
        component: ROUTE_IMPORTS.TaskListView,
        props: (route: RouteLocationNormalized) => ({ ...route.params, viewKind: ViewKind.SEARCH }),
        meta: {
          title: "Search",
          setsPageTitleManually: true,
          setsPageLoadedManually: true,
        },
      },
      {
        path: "f/:folderDuid([a-zA-Z0-9]{12}):slugSep?:slug?",
        component: ROUTE_IMPORTS.FolderRootView,
        props: true,
        children: [
          {
            path: "",
            name: "folder",
            component: ROUTE_IMPORTS.FolderView,
            props: true,
            meta: {
              title: "Folder",
              setsPageTitleManually: true,
            },
          },
        ],
      },
      {
        path: "f/:folderDuid",
        component: ROUTE_IMPORTS.FolderRootView,
        props: true,
        children: [
          {
            path: ":docDuid([a-zA-Z0-9]{12}):slugSep?:slug?",
            name: "doc",
            component: ROUTE_IMPORTS.DocView,
            props: true,
            meta: {
              title: "Doc",
              setsPageTitleManually: true,
            },
          },
        ],
      },
      {
        path: "home",
        component: ROUTE_IMPORTS.HomePageView,
        name: "home",
        meta: {
          title: "Home",
        },
      },
      {
        path: "inbox",
        component: ROUTE_IMPORTS.InboxView,
        name: "inbox",
        meta: {
          title: "Inbox",
        },
      },
      {
        path: "agents",
        component: ROUTE_IMPORTS.AgentList,
        name: "agents",
        meta: {
          title: "Agents",
          minRole: UserRole.MEMBER,
        },
      },
      {
        path: "a/:agentDuid([a-zA-Z0-9]{12})/:agentTab?/:chatDuidAndSlug?",
        component: ROUTE_IMPORTS.AgentDetail,
        name: "agent",
        meta: {
          title: "Agents",
          minRole: UserRole.MEMBER,
          setsPageTitleManually: true,
          modeRoute: true,
        },
      },
      {
        path: "views",
        component: ROUTE_IMPORTS.ViewsRootView,
        name: "views",
        meta: {
          title: "Views",
        },
      },
      {
        path: "dashboards",
        component: ROUTE_IMPORTS.DashboardsRootView,
        name: "dashboards",
        meta: {
          title: "Dashboards",
          minRole: UserRole.MEMBER,
        },
      },
      {
        path: "b/:dashboardDuid([a-zA-Z0-9]{12}):slugSep?:slug?",
        name: "dashboard",
        component: ROUTE_IMPORTS.DashboardView,
        props: true,
        meta: {
          title: "Dashboard",
          setsPageTitleManually: true,
          minRole: UserRole.MEMBER,
        },
      },
      {
        path: "t/:taskDuid([a-zA-Z0-9]{12})/chat/:chatDuidAndSlug?",
        component: ROUTE_IMPORTS.ModeRouteView,
        name: "build_chat",
        meta: {
          title: "Task",
          setsPageLoadedManually: true,
          setsPageTitleManually: true,
          modeRoute: true,
        },
      },
      {
        path: "t/:taskDuidAndSlug?",
        component: ROUTE_IMPORTS.ModeRouteView,
        name: "task",
        meta: {
          title: "Task",
          setsPageLoadedManually: true,
          setsPageTitleManually: true,
          modeRoute: true,
        },
      },
      {
        path: "chat/:chatDuidAndSlug?",
        component: ROUTE_IMPORTS.ModeRouteView,
        name: "project_chat",
        meta: {
          title: "Chat",
          setsPageLoadedManually: true,
          setsPageTitleManually: true,
          modeRoute: true,
        },
      },
      {
        path: "h/:chatDuidAndSlug?",
        component: ROUTE_IMPORTS.ModeRouteView,
        name: "chat",
        meta: {
          title: "Chat",
          setsPageLoadedManually: true,
          setsPageTitleManually: true,
          modeRoute: true,
        },
      },
      {
        path: ":entityKindAbbrev([cou])/:entityDuid([a-zA-Z0-9]{12}):slugSep?:slug?", // spell-checker: disable-line
        name: "entity",
        component: ROUTE_IMPORTS.EntityRedirectView,
        props: true,
        meta: {
          setsPageTitleManually: true,
          setsPageLoadedManually: true,
        },
      },
      {
        path: ":path(.*)*",
        name: "missing",
        component: ROUTE_IMPORTS.PageNotFound,
        meta: {
          title: "Not found",
        },
      },
    ],
  },
  {
    path: "/legal",
    redirect: { name: "missing" },
    component: ROUTE_IMPORTS.LegalView,
    children: [
      {
        path: "privacy-policy",
        name: "privacy",
        component: ROUTE_IMPORTS.PrivacyComponent,
        meta: {
          title: "Dart privacy policy",
          requiresAuth: false,
        },
      },
      {
        path: "terms-of-use",
        name: "terms",
        component: ROUTE_IMPORTS.TermsComponent,
        meta: {
          title: "Dart terms of use",
          requiresAuth: false,
        },
      },
      {
        path: "cookie-policy",
        name: "cookies",
        component: ROUTE_IMPORTS.CookiesComponent,
        meta: {
          title: "Dart cookie policy",
          requiresAuth: false,
        },
      },
    ],
  },
  // TODO refactor these redirects to be all in one place
  {
    path: "/redirect/notion-oauth-finished",
    name: "redirect_notion_oauth_finished",
    redirect: (to) => ({
      ...to,
      name: "home",
      query: {
        settings: "notion",
        ...to.query,
      },
    }),
  },
  {
    path: "/redirect/connector-oauth-finished",
    name: "redirect_connector_oauth_finished",
    component: ROUTE_IMPORTS.ConnectorOauthFinishedView,
    meta: {
      title: "Finishing connector authorization",
      requiresAuth: false,
    },
  },
  {
    path: "/redirect/mobile-app",
    name: "redirect_mobile_app",
    component: ROUTE_IMPORTS.RedirectView,
    meta: {
      title: "Loading Dart mobile app",
      requiresAuth: false,
    },
  },
  {
    path: "/redirect/desktop-app",
    name: "redirect_desktop_app",
    component: ROUTE_IMPORTS.RedirectView,
    meta: {
      title: "Downloading Dart desktop app",
      requiresAuth: false,
    },
  },
];

export default ROUTES;
